/**
 * InventoryRowPendingStrip — FE-248 v2.
 *
 * Inline strip that mounts directly under a GroupInventoryRow when the
 * row's item has 1+ PENDING/WAITLISTED borrow requests from group
 * members. Built to read live state from `borrowRequestsSlice` so the
 * UI updates immediately when:
 *   - the owner approves/declines a request (slice removes the row)
 *   - a new request arrives via push notification (slice prepends)
 *   - the requester cancels their own request (slice removes)
 *
 * Default expansion policy (per Option A spec):
 *   - 1 request   → expanded
 *   - 2 requests  → expanded
 *   - ≥3 requests → collapsed; show summary one-liner; tap to expand
 *   - URGENT/RECALL priority OR expires-soon (<=24h) → force expanded
 *
 * Animations:
 *   - Mount: 200ms ease-out, opacity 0→1 + translateY -6→0
 *   - Per-card approve/decline: 150ms fade-out (handled inside
 *     PendingRequestCard via the slice removing the row → React
 *     unmount path; we let the FlatList LayoutAnimation pick it up)
 *
 * Privacy + data shown follows the "What to provide / not provide"
 * spec — first-initial last-name only, no email, no user_id.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { fpColors } from '../../../styles/theme';
import { PendingRequestCard } from './PendingRequestCard';
import type { BorrowRequestDetails } from '../../../api/borrowTypes';

const EXPIRES_SOON_HOURS = 24;
const AUTO_EXPAND_CAP = 2; // ≤2 requests → expand by default

export interface InventoryRowPendingStripProps {
  /** Pre-filtered + sorted requests for THIS item, from the memoized
   *  per-group selector. Empty/undefined → strip renders nothing. */
  requests: readonly BorrowRequestDetails[];
}

export const InventoryRowPendingStrip: React.FC<
  InventoryRowPendingStripProps
> = ({ requests }) => {
  const count = requests?.length ?? 0;

  // Urgency signals — drive the auto-expand override + summary chip
  const hasUrgent = useMemo(
    () =>
      (requests ?? []).some((r) => {
        const p = String((r as any).priority ?? 'NORMAL').toUpperCase();
        return p === 'URGENT' || p === 'RECALL';
      }),
    [requests],
  );
  const expiringSoonCount = useMemo(
    () =>
      (requests ?? []).filter((r) => {
        const d = (r as any).days_until_expiry;
        return typeof d === 'number' && d <= 1 && d >= 0;
      }).length,
    [requests],
  );

  // Default-expansion decision (see component header)
  const forceExpand = hasUrgent || expiringSoonCount > 0;
  const [expanded, setExpanded] = useState(
    forceExpand || count <= AUTO_EXPAND_CAP,
  );

  // If urgency arrives later (e.g. cron tick into <24h), promote the
  // strip to expanded automatically.
  useEffect(() => {
    if (forceExpand) setExpanded(true);
  }, [forceExpand]);

  // Mount animation — fades+slides the whole strip in when it first
  // becomes non-empty. Re-running renders don't re-animate because the
  // Animated.Values are created once via useRef.
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(-6)).current;
  useEffect(() => {
    if (count === 0) return;
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: 200,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: 0,
        duration: 200,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
    // We deliberately run this once-per-mount; the ref-stable values
    // mean subsequent renders don't restart the animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count > 0]);

  if (count === 0) return null;

  // ── Summary one-liner (collapsed state) ──────────────────────────
  // Priority order: urgency > expiry > waitlist > single
  const nextRequest = requests[0];
  const nextName = formatShortName((nextRequest as any).requester_name);
  const nextTimeAgo = formatTimeAgo((nextRequest as any).created_at);
  const summary = (() => {
    if (hasUrgent) {
      return `${count} pending · ⚠ URGENT`;
    }
    if (expiringSoonCount > 0) {
      return `${count} pending · ⚡ ${expiringSoonCount} ${expiringSoonCount === 1 ? 'expires' : 'expire'} today`;
    }
    if (count > 1) {
      return `${count} pending · ★ ${nextName} next`;
    }
    return `1 pending · ${nextName} · ${nextTimeAgo}`;
  })();

  // ── Render ────────────────────────────────────────────────────────
  return (
    <Animated.View
      style={[
        styles.strip,
        forceExpand && styles.stripUrgent,
        { opacity, transform: [{ translateY }] },
      ]}
      testID={`pending-strip-${(nextRequest as any).item_id ?? 'unknown'}`}
    >
      <Pressable
        onPress={() => setExpanded((v) => !v)}
        accessibilityRole="button"
        accessibilityLabel={
          expanded ? 'Collapse pending requests' : 'Expand pending requests'
        }
        accessibilityState={{ expanded }}
        style={styles.header}
        hitSlop={6}
      >
        <View style={styles.headerLeft}>
          <MaterialIcons
            name="hourglass-empty"
            size={14}
            color={
              forceExpand
                ? ((fpColors as any).onErrorContainer ?? fpColors.error)
                : fpColors.primary
            }
          />
          <Text
            style={[
              styles.headerText,
              forceExpand && styles.headerTextUrgent,
            ]}
            numberOfLines={1}
          >
            {summary}
          </Text>
        </View>
        <MaterialIcons
          name={expanded ? 'expand-less' : 'expand-more'}
          size={20}
          color={fpColors.onSurfaceVariant}
        />
      </Pressable>

      {expanded && (
        <View style={styles.cards}>
          {count > 1 ? (
            <Text style={styles.divider}>★ NEXT IN LINE</Text>
          ) : null}
          <PendingRequestCard
            request={nextRequest}
            variant="item"
          />
          {count > 1 && (
            <>
              <Text style={[styles.divider, styles.dividerWaitlist]}>
                WAITLIST ({count - 1})
              </Text>
              {requests.slice(1).map((req) => (
                <PendingRequestCard
                  key={(req as any).id}
                  request={req}
                  variant="item"
                />
              ))}
            </>
          )}
        </View>
      )}
    </Animated.View>
  );
};

// ── helpers ──────────────────────────────────────────────────────────

/** Format "Dylan Kowalski" → "Dylan K." Fallback to the trimmed full
 *  name if it doesn't split cleanly. */
function formatShortName(full: string | null | undefined): string {
  const trimmed = (full ?? '').trim();
  if (!trimmed) return 'Someone';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]} ${parts[1][0].toUpperCase()}.`;
  }
  return parts[0];
}

function formatTimeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── styles ───────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  strip: {
    backgroundColor:
      (fpColors as any).surfaceContainerLow ??
      fpColors.surfaceVariant ??
      '#F7F2FA',
    borderRadius: 12,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginTop: 6,
    marginHorizontal: 4, // inset under the parent row for visual nesting
    overflow: 'hidden',
  },
  stripUrgent: {
    backgroundColor: (fpColors as any).errorContainer ?? '#FFDAD6',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    minHeight: 24,
  },
  headerLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  headerText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.onSurface,
    letterSpacing: 0.1,
  },
  headerTextUrgent: {
    color: (fpColors as any).onErrorContainer ?? fpColors.error,
  },
  cards: {
    marginTop: 10,
  },
  divider: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    color: fpColors.onSurfaceVariant,
    letterSpacing: 0.6,
    marginBottom: 6,
    marginTop: 4,
  },
  dividerWaitlist: {
    marginTop: 8,
    color: fpColors.onSurfaceVariant,
  },
});
